import React, { useState, useEffect } from "react";
import Layouts from "../layouts/Layouts";
import PageBanner from "../components/PageBanner";
import { useIsMobile } from "../components/useIsMobile";
import { Link, useNavigate } from "react-router-dom";
import { db } from "../firebase";
import {
  collection,
  getDocs,
  getCountFromServer,
  limit,
  orderBy,
  query,
  startAfter,
  where,
} from "firebase/firestore";
import SearchComponent from "../components/SearchComponent";
import { BiChevronsRight } from "react-icons/bi";
import { useTranslation } from "react-i18next";

const Blog = () => {
  const isMobile = useIsMobile();
  const navigate = useNavigate();
  const [blogs, setBlogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [lastVisible, setLastVisible] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [activeBlogId, setActiveBlogId] = useState(null);
  const [sortBy, setSortBy] = useState("createdAt"); // instead of "title"
  const [sortOrder, setSortOrder] = useState("desc"); // instead of "asc"
  const [cursors, setCursors] = useState([null]);
  const PAGE_SIZE = 2;
  const { t } = useTranslation();

  const fetchTotalBlogCount = async () => {
    try {
      const q = collection(db, "blogs");
      const snapshot = await getCountFromServer(q);
      const totalBlogsCount = snapshot.data().count;
      setTotalPages(Math.ceil(totalBlogsCount / PAGE_SIZE));
    } catch (error) {
      console.error("Error fetching total blog count: ", error);
    }
  };
  const fetchBlogs = async (page = 1) => {
    setLoading(true);
    try {
      let q = query(
        collection(db, "blogs"),
        orderBy(sortBy, sortOrder),
        limit(PAGE_SIZE)
      );

      if (searchTerm) {
        q = query(
          q,
          where("title", ">=", searchTerm),
          where("title", "<=", searchTerm + "\uf8ff")
        );
      }

      if (page > 1 && cursors[page - 1]) {
        q = query(q, startAfter(cursors[page - 1]));
      }

      const querySnapshot = await getDocs(q);
      const newBlogs = querySnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));

      // Cache images using localStorage
      const updatedBlogs = newBlogs.map((blog) => {
        const cachedImageUrl = localStorage.getItem(blog.images[0]);
        const imageUrl = cachedImageUrl ? cachedImageUrl : blog.images[0];

        if (!cachedImageUrl) {
          localStorage.setItem(blog.images[0], imageUrl); // Cache the image URL if not cached
        }

        return { ...blog, images: [imageUrl] };
      });

      const lastVisibleDoc = querySnapshot.docs[querySnapshot.docs.length - 1];

      setCursors((prevCursors) => {
        const updatedCursors = [...prevCursors];
        updatedCursors[page] = lastVisibleDoc;
        return updatedCursors;
      });

      setBlogs(updatedBlogs);
    } catch (error) {
      console.error("Error fetching blogs: ", error);
    } finally {
      setLoading(false);
    }
  };

  const handlePageClick = (pageNumber) => {
    setCurrentPage(pageNumber);
    fetchBlogs(pageNumber);
  };

  useEffect(() => {
    fetchTotalBlogCount();
    fetchBlogs(currentPage);
  }, [currentPage, sortBy, sortOrder]);

  useEffect(() => {
    setCursors([null]);
    setCurrentPage(1);
    fetchBlogs(1);
  }, [searchTerm, sortBy, sortOrder]);

  const handleBlogClick = (blogItem) => {
    navigate("/blog-details", { state: { blogItem } });
  };

  const stripHtmlTags = (text) => text?.replace(/<\/?[^>]+(>|$)/g, "");

  const truncateText = (text, length = 150) => {
    const plainText = stripHtmlTags(text);
    if (plainText?.length > length) {
      return plainText.substring(0, length) + "...";
    }
    return plainText;
  };

  const formatDate = (date) => {
    if (date && date.seconds) {
      const fullDate = new Date(date.seconds * 1000);
      const options = {
        weekday: "long",
        year: "numeric",
        month: "long",
        day: "numeric",
      };
      return fullDate.toLocaleDateString(undefined, options);
    }
    return "N/A";
  };

  const handleSortChange = (e) => {
    const [field, order] = e.target.value.split("-");
    setSortBy(field);
    setSortOrder(order);
  };

  return (
    <Layouts footer={2} noheader>
      {/**
      <PageBanner
        title={t("pageBanner.blogs")}
        pageName={t("pageBanner.blogs")}
        pageNumber="6"
      />
     */}{" "}
      <section style={{ padding: "60px 0", backgroundColor: "#f9f9f9" }}>
        <div
          style={{
            maxWidth: "1300px",
            margin: "0 auto",
            padding: isMobile ? "2vw" : "10px",
          }}
        >
          <SearchComponent setSearchTerm={setSearchTerm} />

          {/* Sorting Dropdown */}
          <div style={{ marginBottom: "20px" }}>
            <select
              onChange={handleSortChange}
              value={`${sortBy}-${sortOrder}`}
            >
              <option value="title-asc">Sort by Title (A-Z)</option>
              <option value="title-desc">Sort by Title (Z-A)</option>
              <option value="createdAt-desc">
                Sort by Date (Newest First)
              </option>
              <option value="createdAt-asc">Sort by Date (Oldest First)</option>
            </select>
          </div>

          <div
            style={{
              display: "flex",
              flexWrap: "wrap",
              gap: "30px",
              padding: isMobile && "1vw",
            }}
          >
            {loading && blogs.length === 0 && <p>Loading blogs...</p>}
            {blogs.map((blog) => (
              <div
                key={blog.id}
                style={{
                  display: "flex",
                  flexDirection: "column",
                  border: "1px solid #ddd",
                  borderRadius: "8px",
                  boxShadow:
                    activeBlogId === blog.id
                      ? "0 4px 12px rgba(0, 0, 0, 0.3)"
                      : "0 2px 10px rgba(0, 0, 0, 0.1)",
                  marginBottom: "20px",
                  overflow: "hidden",
                  cursor: "pointer",
                  flex: "1 1 48%",
                  minWidth: "300px",
                  maxWidth: "635px",
                  transform:
                    activeBlogId === blog.id ? "scale(0.98)" : "scale(1)",
                  transition: "transform 0.2s, box-shadow 0.2s",
                }}
                onMouseDown={() => setActiveBlogId(blog.id)}
                onMouseUp={() => setActiveBlogId(null)}
                onMouseLeave={() => setActiveBlogId(null)}
                onClick={() => handleBlogClick(blog)}
              >
                <div
                  style={{
                    width: "100%",
                    height: "280px",
                    minHeight: "250px",
                  }}
                >
                  <img
                    src={blog.images[0]}
                    alt={blog.title}
                    style={{
                      width: "100%",
                      height: "100%",
                      objectFit: "cover",
                      objectPosition: "0% 34%",
                    }}
                  />
                </div>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    flexDirection: "column",
                    height: "100%",
                  }}
                >
                  <div
                    style={{
                      padding: "20px",
                      height: "100%",
                      display: "flex",
                      justifyContent: "space-between",
                      flexDirection: "column",
                    }}
                  >
                    <div>
                      <h3
                        style={{
                          fontSize: "1.75rem",
                          color: "#34495e",
                          marginBottom: "10px",
                          display: "-webkit-box",
                          WebkitBoxOrient: "vertical",
                          overflow: "hidden",
                          WebkitLineClamp: 2,
                          lineHeight: "1.2",
                          height: "calc(1.2em * 2)",
                        }}
                      >
                        {blog.title}
                      </h3>
                      <div
                        style={{
                          fontSize: "1.1rem",
                          color: "#7f8c8d",
                          flex: 1,
                        }}
                        dangerouslySetInnerHTML={{
                          __html: truncateText(blog.description, 150),
                        }}
                      />
                    </div>

                    <div
                      style={{
                        fontSize: "0.9rem",
                        color: "#95a5a6",
                        marginBottom: "15px",
                      }}
                    >
                      <span>{formatDate(blog.createdAt)}</span>
                    </div>
                  </div>
                  <Link
                    style={{
                      fontSize: "16px",
                      color: "white",
                      textDecoration: "none",
                      display: "inline-flex",
                      width: "100%",
                      justifyContent: "end",
                      alignItems: "center",
                      backgroundColor: "var(--color-fourth)",
                      padding: "12px",
                    }}
                    className="read-more"
                  >
                    Check Full Article
                    <BiChevronsRight fontSize={23} />
                  </Link>
                </div>
              </div>
            ))}
          </div>

          {/* Pagination */}
          <div style={{ textAlign: "center", marginTop: "20px" }}>
            {Array.from({ length: totalPages }, (_, index) => index + 1).map(
              (pageNumber) => (
                <button
                  key={pageNumber}
                  onClick={() => handlePageClick(pageNumber)}
                  style={{
                    margin: "0 5px",
                    padding: "10px 15px",
                    backgroundColor:
                      currentPage === pageNumber
                        ? "var(--color-fourth)"
                        : "#ddd",
                    color: currentPage === pageNumber ? "white" : "black",
                    border: "none",
                    borderRadius: "5px",
                    cursor: "pointer",
                  }}
                >
                  {pageNumber}
                </button>
              )
            )}
          </div>

          {blogs.length === 0 && !loading && (
            <p style={{ textAlign: "center" }}>No blogs found.</p>
          )}
        </div>
      </section>
    </Layouts>
  );
};

export default Blog;
